'use client';

import { useRouter, useSearchParams } from 'next/navigation';
import { X } from 'lucide-react';
import { useTranslation } from 'react-i18next';

import { Button } from '@kit/ui/button';

import { parseRadius } from '../_lib/location-params';
import { saveLocationOff } from '../_lib/location-preference';

interface ActiveFiltersProps {
  /** slug → display name map (groups + leaves) for the category chip label. */
  categoryNames: Record<string, string>;
}

const CONDITION_LABEL_KEYS: Record<string, string> = {
  new: 'explore:activeFilters.conditionNew',
  like_new: 'explore:activeFilters.conditionLikeNew',
  good: 'explore:activeFilters.conditionGood',
  fair: 'explore:activeFilters.conditionFair',
};

/**
 * Removable active-filter chips + "Clear all", shown above the results grid on
 * both breakpoints. Baymard: always show applied filters (20% of sites fail
 * to, causing disorientation), with removable chips AND a clear-all. Reads the
 * URL as the single source of truth; each × deletes one param (and the keyset
 * cursor) and pushes the new URL, which re-runs the server search.
 *
 * `q` (search) is intentionally NOT shown as a chip — it has its own always-
 * visible input; a chip would duplicate it.
 */
export function ActiveFilters({ categoryNames }: ActiveFiltersProps) {
  const router = useRouter();
  const searchParams = useSearchParams();
  const { t } = useTranslation();

  const splitParam = (key: string): string[] =>
    (searchParams.get(key) ?? '')
      .split(',')
      .map((s) => s.trim())
      .filter(Boolean);

  const category = searchParams.get('category');
  const brand = searchParams.get('brand');
  const priceMin = searchParams.get('priceMin');
  const priceMax = searchParams.get('priceMax');
  const near = searchParams.get('near');
  const city = searchParams.get('city');
  const pickupOnly = searchParams.get('delivery') === 'pickup';

  // Multi-select facets render one removable chip PER selected value.
  const chips: Array<{
    id: string;
    key: string;
    value?: string;
    label: string;
  }> = [];
  if (near) {
    const radius = parseRadius(searchParams.get('radius') ?? undefined);
    chips.push({
      id: 'location',
      key: 'location',
      label: `${city ?? 'Near me'} · ${radius} km`,
    });
  }
  if (pickupOnly) {
    chips.push({
      id: 'delivery',
      key: 'delivery',
      label: 'Local pickup only',
    });
  }
  if (category) {
    const name = categoryNames[category] ?? category;
    chips.push({ id: 'category', key: 'category', label: name });
  }
  for (const c of splitParam('condition')) {
    const labelKey = CONDITION_LABEL_KEYS[c];
    chips.push({
      id: `condition:${c}`,
      key: 'condition',
      value: c,
      label: labelKey ? t(labelKey) : c,
    });
  }
  if (brand)
    chips.push({
      id: 'brand',
      key: 'brand',
      label: t('explore:activeFilters.brand', { brand }),
    });
  for (const c of splitParam('color')) {
    chips.push({ id: `color:${c}`, key: 'color', value: c, label: c });
  }
  for (const s of splitParam('size')) {
    chips.push({
      id: `size:${s}`,
      key: 'size',
      value: s,
      label: t('explore:activeFilters.size', { size: s }),
    });
  }
  if (priceMin || priceMax) {
    const label =
      priceMin && priceMax
        ? t('explore:activeFilters.priceRange', {
            min: priceMin,
            max: priceMax,
          })
        : priceMin
          ? t('explore:activeFilters.priceFrom', { min: priceMin })
          : t('explore:activeFilters.priceUpTo', { max: priceMax });
    chips.push({ id: 'price', key: 'price', label });
  }

  if (chips.length === 0) return null;

  function removeChip(key: string, value?: string) {
    const params = new URLSearchParams(searchParams.toString());
    params.delete('cursor');
    if (key === 'price') {
      params.delete('priceMin');
      params.delete('priceMax');
    } else if (key === 'location') {
      // Persist the opt-out, or the auto-detect re-applies on next paint and
      // the chip's × looks broken.
      saveLocationOff();
      params.delete('near');
      params.delete('radius');
      params.delete('city');
    } else if (value !== undefined) {
      // Multi-select: drop just this value, keep the rest of the facet.
      const next = (params.get(key) ?? '')
        .split(',')
        .map((s) => s.trim())
        .filter((v) => v && v !== value);
      if (next.length) {
        params.set(key, next.join(','));
      } else {
        params.delete(key);
      }
    } else {
      params.delete(key);
    }
    const qs = params.toString();
    router.push(qs ? `/marketplace?${qs}` : '/marketplace');
  }

  function clearAll() {
    // Preserve only the free-text search; drop every facet + cursor. The
    // location opt-out must persist too, or auto-detect re-applies instantly.
    if (near) saveLocationOff();
    const params = new URLSearchParams();
    const q = searchParams.get('q');
    if (q) params.set('q', q);
    const qs = params.toString();
    router.push(qs ? `/marketplace?${qs}` : '/marketplace');
  }

  return (
    <div
      className="flex flex-wrap items-center gap-2"
      data-testid="active-filters"
    >
      {chips.map((chip) => (
        <Button
          key={chip.id}
          type="button"
          variant="outline"
          size="sm"
          onClick={() => removeChip(chip.key, chip.value)}
          className="border-border bg-muted/60 hover:bg-muted inline-flex items-center gap-1.5 rounded-full border py-1 pr-2 pl-3 text-sm capitalize transition-colors"
        >
          <span>{chip.label}</span>
          <X
            className="size-3.5"
            aria-label={t('explore:activeFilters.removeFilter', {
              label: chip.label,
            })}
          />
        </Button>
      ))}
      <Button
        type="button"
        variant="link"
        onClick={clearAll}
        className="text-muted-foreground hover:text-foreground ml-1 h-auto p-0 text-sm underline-offset-4 hover:underline"
      >
        {t('explore:activeFilters.clearAll')}
      </Button>
    </div>
  );
}
